import { faLink, faTrash } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useContext, useState } from 'react'
import z from 'zod'
import { useOrderApplicantStatusMutation } from '~/api/mutations/order'
import { getApiError } from '~/api/problem'
import {
  Dropdown,
  EditorInput,
  Form,
  InputCol,
  Submit
} from '~/components/Form'
import { SubmitAlignment } from '~/components/Form/SubmitAlignment'
import { Badge } from '~/components/Global/Blocks/Badge/Badge'
import {
  Button,
  ButtonVariation
} from '~/components/Global/Interactions/Button'
import { ToastType } from '~/components/Global/Interactions/Toast/ToastType'
import { AsideAction } from '~/components/Scopes/Actions/AsideAction'
import { ActionErrorType } from '~/domains/actions/errors'
import { OrdersAutocomplete } from '~/domains/orders/components/OrdersAutocomplete'
import { useTaxonomy } from '~/domains/taxonomies/queries/useTaxonomy'
import { UsersAutocomplete } from '~/domains/users/components/UsersAutocomplete'
import { isHtmlEmpty, toast } from '~/utils'
import { AsideActionContext } from '~/utils/contextes'
import { AsideActionsItems } from './AsideActionsItems'
import { useCloseAction } from './hooks/useCloseAction'

const formSchema = z.object({
  orderId: z.coerce.number('Commande requise').min(1, 'Commande requise'),
  statusId: z.coerce.number('Statut requis').min(1, 'Statut requis'),
  note: z.string().optional()
})

type FormValues = z.infer<typeof formSchema>

type AddToOrderActionProps = {
  applicantIds: number[]
}

export const AddToOrderAction = ({ applicantIds }: AddToOrderActionProps) => {
  const { setIsDirty } = useContext(AsideActionContext)
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null)
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null)
  const closeAction = useCloseAction()

  const { data: applicantOrderStatuses, isLoading: isLoadingStatuses } =
    useTaxonomy('applicant_order_statuses')

  const addApplicantToOrderStatusMutation = useOrderApplicantStatusMutation(
    selectedOrderId ?? undefined
  )

  const onSubmit = async (values: FormValues) =>
    addApplicantToOrderStatusMutation.mutateAsync(
      {
        applicants: applicantIds,
        status: values.statusId,
        note: isHtmlEmpty(values.note) ? undefined : values.note
      },
      {
        onSuccess: () => {
          toast(
            applicantIds.length > 1 ? 'Candidats associés' : 'Candidat associé',
            applicantIds.length > 1
              ? 'Candidats associés avec succès.'
              : 'Candidat associé avec succès.',
            ToastType.Success
          )
          closeAction(true)
        },
        onError: (error) => {
          const problem = getApiError(error)
          if (problem?.type === ActionErrorType.LimitExceeded) {
            const limit = problem.extensions.limit
            toast(
              'Limite dépassée',
              typeof limit === 'number'
                ? `Vous ne pouvez pas associer plus de ${limit} candidat${limit > 1 ? 's' : ''} à la fois`
                : 'Vous ne pouvez pas associer autant de candidat à la fois',
              ToastType.Danger
            )
            return
          }

          toast(
            'Erreur',
            "Impossible d'associer les candidats à la commande. Veuillez réessayer.",
            ToastType.Danger
          )
        }
      }
    )

  return (
    <Form onSubmit={onSubmit} schema={formSchema} defaultValues={{ note: '' }}>
      <AsideAction
        title={'Associer à une commande'}
        icon={faLink}
        applicantIds={applicantIds}
      >
        <AsideActionsItems>
          <Button
            variation={ButtonVariation.DangerInversed}
            type="button"
            onClick={(e) => {
              e.preventDefault()
              closeAction()
            }}
            buttonSize="small"
          >
            <FontAwesomeIcon icon={faTrash} />
          </Button>
          <Submit
            buttonVariation={ButtonVariation.Primary}
            submitAlignment={SubmitAlignment.Right}
            loadingText="Association en cours..."
            successText="Associé !"
            onSuccessReset={false}
            buttonSize="small"
          >
            <FontAwesomeIcon icon={faLink} />
            Associer
          </Submit>
        </AsideActionsItems>
        <InputCol>
          <UsersAutocomplete
            label="Filtrer par utilisateur"
            placeholder="Choisir un utilisateur"
            onChange={(value) => {
              setSelectedUserId(value ? Number.parseInt(value, 10) : null)
              setIsDirty(true)
            }}
          />

          <OrdersAutocomplete
            label="Commandes ouvertes"
            name="orderId"
            required
            placeholder="Choisir une commande"
            userId={selectedUserId ?? undefined}
            onChange={(value) => {
              setSelectedOrderId(value ? Number.parseInt(value, 10) : null)
              setIsDirty(true)
            }}
          />

          <Dropdown
            label="Statut"
            name="statusId"
            placeholder="Choisir un statut"
            required
            options={
              applicantOrderStatuses
                ?.filter((status) => status.specialStatus !== 'accepted')
                .map((status) => ({
                  label: status.name,
                  element: (
                    <Badge title={status.name} backgroundColor={status.color} />
                  ),
                  value: status.id.toString()
                })) ?? []
            }
            defaultValue={applicantOrderStatuses
              ?.find((status) => status.specialStatus === 'selected')
              ?.id.toString()}
            optionsLoading={isLoadingStatuses}
            emptyMessage="Aucun statut disponible"
            onChange={() => setIsDirty(true)}
          />

          <EditorInput
            label="Commentaire"
            name="note"
            showBottomMenu={false}
            isReduced
            autofocus={false}
            placeholder="Saisir un commentaire sur la commande..."
            onChange={() => setIsDirty(true)}
          />
        </InputCol>
      </AsideAction>
    </Form>
  )
}
